import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import axios from "axios";
import {
  Printer, MessageCircle, Loader2, Scale, Home, Phone, Globe,
  CheckCircle2, AlertTriangle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── Legal & Property lists — kept in sync with the PDF (server.py) ─── */
const LEGAL_SERVICES = [
  "Sale Agreement", "Registered Rent Agreement", "Partnership Deed", "Gift Deed",
  "Release Deed", "Will Deed", "Power of Attorney", "Notice of Intimation (NOI)",
  "Newspaper Notification", "Income Tax Return (ITR) Filing",
  "Trust Registration", "Society Registration",
];
const PROPERTY_SERVICES = [
  "Residential & Commercial Property", "Property Documentation Guidance",
  "Sale", "Purchase", "Rent", "Flat", "Shop", "Plot",
  "Property Valuation", "Legal Verification of Property",
  "Site Visit & Consultation", "End-to-End Property Support",
];

export default function ReceiptView() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/public/receipts/${id}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Receipt not found"));
  }, [id]);

  if (err) return <div className="p-10 text-center text-red-600 font-semibold">{err}</div>;
  if (!data) return (
    <div className="p-10 flex items-center justify-center gap-2 text-slate-400">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading receipt…
    </div>
  );

  const r = data.receipt;
  const i = data.issuer || {};
  const shareUrl = window.location.href;

  const doPrint = () => window.print();

  const doWhatsApp = () => {
    const msg = [
      `*${i.name || "SHREE SAMARTH CONSULTANCY"}* — Receipt Slip`,
      i.phone ? `Contact: ${i.phone}` : "",
      i.address ? `Address: ${i.address}` : "",
      ``,
      `*Receipt No:* ${shortDocNo(r.doc_no)}`,
      `*Date:* ${formatDate(r.doc_date)}`,
      `*Customer:* ${r.customer_name || "-"}`,
      `*Mobile:* ${r.mobile || "-"}`,
      `*Service:* ${r.service_name || "-"}`,
      ``,
      `View full receipt online: ${shareUrl}`,
      ``,
      `Thank you for your Service!`,
    ].filter(Boolean).join("\n");
    const number = (r.mobile || "").replace(/\D/g, "");
    const numPart = number ? `91${number}` : "";
    window.open(`https://wa.me/${numPart}?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-screen bg-slate-100 py-6 px-4 print:bg-white print:py-0">
      <div className="max-w-2xl mx-auto">
        {/* Action bar — hidden when printing */}
        <div className="flex justify-end gap-2 mb-4 print:hidden">
          <button
            onClick={doWhatsApp}
            data-testid="receipt-whatsapp-btn"
            className="h-10 px-4 rounded-md bg-[#25D366] hover:bg-[#1FB258] text-white font-semibold flex items-center gap-2 shadow-sm"
          >
            <MessageCircle className="h-4 w-4" /> Send via WhatsApp
          </button>
          <button
            onClick={doPrint}
            data-testid="receipt-print-btn"
            className="h-10 px-4 rounded-md bg-slate-800 hover:bg-slate-900 text-white font-semibold flex items-center gap-2 shadow-sm"
          >
            <Printer className="h-4 w-4" /> Print
          </button>
        </div>

        {/* Receipt Card */}
        <div className="bg-white rounded-md shadow-sm border border-slate-200 overflow-hidden print:shadow-none print:border-0" data-testid="receipt-slip">
          {/* Urgent banner */}
          {r.is_urgent && (
            <div className="bg-red-600 text-white px-4 py-2 flex items-center gap-2 text-sm font-semibold" data-testid="receipt-urgent-banner">
              <AlertTriangle className="h-4 w-4" />
              URGENT WORK{r.urgent_reason ? ` — ${r.urgent_reason}` : ""}
            </div>
          )}

          {/* Header */}
          <div className="px-6 py-5 border-b border-slate-200 flex items-start gap-5">
            <div className="shrink-0 border border-slate-200 rounded p-2 bg-white text-center">
              <QRCodeSVG value={shareUrl} size={82} level="M" includeMargin={false} />
              <div className="text-[9px] text-slate-500 mt-1 tracking-wide">Scan to verify</div>
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-display text-2xl font-black tracking-tight text-slate-900 leading-tight">
                {i.name || "SHREE SAMARTH CONSULTANCY"}
              </h1>
              {i.address && (
                <div className="text-sm text-slate-600 mt-1 leading-snug">
                  <span className="font-semibold">Address:</span> {i.address}
                </div>
              )}
              {i.phone && (
                <div className="text-sm text-slate-600 mt-1">
                  <span className="font-semibold">Contact:</span> <span className="font-mono-num">{i.phone}</span>
                </div>
              )}
              {i.email && (
                <div className="text-xs text-slate-500 mt-1"><span className="font-semibold">Email:</span> {i.email}</div>
              )}
              <div className="text-xs text-slate-600 mt-1"><span className="font-semibold">Office Hours:</span> Mon to Sun, 10:00 AM to 8:00 PM</div>
            </div>
          </div>

          {/* Receipt No / Date / Type */}
          <div className="px-6 py-3 grid grid-cols-3 border-b border-slate-200 text-sm">
            <div>Receipt No: <span className="font-bold font-mono-num text-slate-900" data-testid="rv-docno">{shortDocNo(r.doc_no)}</span></div>
            <div className="text-center">Date: <span className="font-bold text-slate-900">{formatDate(r.doc_date)}</span></div>
            <div className="text-right">Type: <span className="font-bold text-slate-900">{r.type || "Service"}</span></div>
          </div>

          {/* Customer / Mobile */}
          <div className="px-6 py-3 grid grid-cols-2 border-b border-slate-200 text-sm">
            <div>Customer: <span className="font-bold uppercase tracking-wide text-slate-900">{r.customer_name || "-"}</span></div>
            <div className="text-right">Mobile: <span className="font-bold font-mono-num text-slate-900">{r.mobile || "-"}</span></div>
          </div>

          {/* Service block */}
          <div className="px-6 py-4">
            <div className="border border-slate-200 rounded-md overflow-hidden">
              <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 font-semibold text-slate-700 text-sm">
                Service Name
              </div>
              <div className="px-4 py-3">
                <div className="font-semibold text-slate-900">{r.service_name || "-"}</div>
                {r.application_id && (
                  <div className="text-xs text-slate-500 italic mt-0.5">App ID: {r.application_id}</div>
                )}
              </div>
            </div>
          </div>

          {/* Services Directory — same as PDF */}
          <div className="px-6 pb-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ServiceColumn
                icon={<Scale className="h-4 w-4" />}
                title="LEGAL SERVICES"
                phone="9209463396"
                items={LEGAL_SERVICES}
                headerBg="bg-slate-900"
              />
              <ServiceColumn
                icon={<Home className="h-4 w-4" />}
                title="PROPERTY CONSULTANT"
                phone="9833852433"
                items={PROPERTY_SERVICES}
                headerBg="bg-[#B45309]"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-200 text-center text-xs text-slate-500 space-y-1">
            <div className="italic">Note: Documents, certificates & cards will not be issued without providing a receipt.</div>
            <div className="pt-2 flex items-center justify-center gap-2 text-slate-700 font-semibold">
              <Globe className="h-3.5 w-3.5" /> Website: <a href="https://www.sconsultancy.in" target="_blank" rel="noreferrer" className="text-[color:var(--brand-teal)] underline">www.sconsultancy.in</a>
              <span className="text-slate-300">|</span>
              <span>Thank you for choosing {i.name || "SHREE SAMARTH CONSULTANCY"}</span>
            </div>
            <div className="pt-1">System Generated on: {formatDateTime(r.created_at)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ────── small helpers ────── */
function TR({ label, value, className = "", bold = false }) {
  return (
    <tr>
      <td className="px-4 py-2 text-slate-600 w-1/2">{label}</td>
      <td className={`px-4 py-2 text-right font-mono-num ${bold ? "font-bold text-slate-900" : ""} ${className}`}>{value}</td>
    </tr>
  );
}
function StatusPill({ status }) {
  const map = {
    Pending:      "bg-amber-100 text-amber-800 border-amber-200",
    "In Progress":"bg-blue-100 text-blue-800 border-blue-200",
    Completed:    "bg-emerald-100 text-emerald-800 border-emerald-200",
    Delivered:    "bg-emerald-600 text-white border-emerald-700",
  };
  const cls = map[status] || "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${cls}`}>
      {status === "Delivered" && <CheckCircle2 className="h-3 w-3" />}{status}
    </span>
  );
}
function ServiceColumn({ icon, title, phone, items, headerBg }) {
  return (
    <div className="border border-slate-200 rounded-md overflow-hidden">
      <div className={`${headerBg} text-white px-3 py-1.5 text-center text-xs font-bold tracking-wide flex items-center justify-center gap-1.5`}>
        {icon}{title}
      </div>
      <div className="bg-slate-50 px-3 py-1 text-center text-[11px] font-bold text-[#B45309] flex items-center justify-center gap-1">
        <Phone className="h-3 w-3" /> Contact: {phone}
      </div>
      <ul className="px-3 py-2 space-y-0.5 text-[11px] text-slate-700">
        {items.map((s) => (
          <li key={s} className="leading-snug">• {s}</li>
        ))}
        <li className="leading-snug pt-1 text-emerald-700 font-bold">✓ Home Visit Service Available</li>
      </ul>
    </div>
  );
}
function inr(n) {
  const v = Number(n || 0);
  return `Rs. ${v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function formatDate(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}
function shortDocNo(v) {
  if (!v) return "-";
  const parts = String(v).split("/");
  const last = parts[parts.length - 1] || String(v);
  const m = last.match(/\d+/);
  return (m ? m[0] : last).padStart(7, "0");
}
function formatDateTime(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
